// Section 24.12 and ADR-0008: a save replays on the config it was played with. The UI shows a
// "played on an older balance" note when that config differs from the current one and offers to
// continue on the saved config or fork onto the current one.
import { configSnapshot, contentVersion, type Config } from '../sim/api';
import type { SaveEnvelope } from './envelope';

type Json = unknown;

function isPlainObject(x: Json): x is Record<string, Json> {
  return x !== null && typeof x === 'object' && !Array.isArray(x);
}

/** Stable JSON: object keys sorted, so key order never makes two configs "differ". */
export function stableStringify(x: Json): string {
  if (Array.isArray(x)) return `[${x.map(stableStringify).join(',')}]`;
  if (isPlainObject(x))
    return `{${Object.keys(x)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableStringify(x[k])}`)
      .join(',')}}`;
  return JSON.stringify(x);
}

function hasMissingKeys(saved: Json, current: Json): boolean {
  if (!isPlainObject(current)) return false;
  if (!isPlainObject(saved)) return true;
  return Object.keys(current).some((k) => !(k in saved) || hasMissingKeys(saved[k], current[k]));
}

function fillMissing(saved: Json, current: Json): Json {
  if (!isPlainObject(current) || !isPlainObject(saved)) return saved;
  const out: Record<string, Json> = { ...saved };
  for (const [k, v] of Object.entries(current))
    out[k] = k in saved ? fillMissing(saved[k], v) : structuredClone(v);
  return out;
}

/**
 * The saved config with any keys added since the save filled from the current config (F-12).
 * Returns the saved object itself when nothing is missing, so registries cached per config
 * object are reused.
 */
export function completeConfig(saved: Config, current: Config = configSnapshot()): Config {
  return hasMissingKeys(saved, current) ? (fillMissing(saved, current) as Config) : saved;
}

export interface BalanceCheck {
  /** The saved config differs from the current one (a balance change since the save). */
  configDiffers: boolean;
  /** The registries' static data changed since the save. */
  contentDiffers: boolean;
}

export function balanceCheck(
  envelope: Pick<SaveEnvelope, 'configSnapshot' | 'contentVersion'>,
): BalanceCheck {
  return {
    configDiffers: stableStringify(envelope.configSnapshot) !== stableStringify(configSnapshot()),
    contentDiffers: envelope.contentVersion !== contentVersion(),
  };
}
